import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { adminApi } from "../../lib/adminApi";
import { AdminPageHeader } from "../../components/admin/FormPrimitives";
import {
    Inbox,
    Newspaper,
    FileText,
    Image as ImageIcon,
    ArrowRight,
} from "lucide-react";

function StatCard({ label, value, icon: Icon, to, testId }) {
    return (
        <Link
            to={to}
            className="card-line p-6 flex items-start gap-4 group"
            data-testid={testId}
        >
            <div className="w-12 h-12 flex items-center justify-center border border-gold/60 bg-ink shrink-0">
                <Icon size={22} className="text-gold" strokeWidth={1.5} />
            </div>
            <div className="flex-1">
                <div className="text-xs uppercase tracking-widest text-neutral-500">
                    {label}
                </div>
                <div className="font-serif text-3xl text-neutral-50 mt-1">
                    {value}
                </div>
            </div>
            <ArrowRight
                size={16}
                className="text-gold opacity-0 group-hover:opacity-100 transition-opacity"
            />
        </Link>
    );
}

export default function AdminDashboard() {
    const [stats, setStats] = useState({
        unread: 0,
        totalMessages: 0,
        published: 0,
        totalPosts: 0,
    });

    useEffect(() => {
        (async () => {
            try {
                const [msgs, posts] = await Promise.all([
                    adminApi.get("/admin/messages"),
                    adminApi.get("/admin/blog-posts"),
                ]);
                const all = msgs.data || [];
                const bp = posts.data || [];
                setStats({
                    unread: all.filter((m) => !m.read).length,
                    totalMessages: all.length,
                    published: bp.filter((p) => p.published).length,
                    totalPosts: bp.length,
                });
            } catch {
                /* ignore */
            }
        })();
    }, []);

    return (
        <div data-testid="admin-dashboard">
            <AdminPageHeader
                title="Tableau de bord"
                subtitle="Aperçu rapide de l'activité du site et raccourcis vers les zones d'édition."
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <StatCard
                    label="Messages non lus"
                    value={stats.unread}
                    icon={Inbox}
                    to="/admin/messages"
                    testId="stat-unread"
                />
                <StatCard
                    label="Articles publiés"
                    value={`${stats.published} / ${stats.totalPosts || 0}`}
                    icon={Newspaper}
                    to="/admin/blog"
                    testId="stat-blog"
                />
                <StatCard
                    label="Contenus éditoriaux"
                    value="Site"
                    icon={FileText}
                    to="/admin/contenus"
                    testId="stat-content"
                />
                <StatCard
                    label="Images du site"
                    value="Logo · Photo"
                    icon={ImageIcon}
                    to="/admin/images"
                    testId="stat-images"
                />
            </div>
            <div className="mt-10 card-line p-6">
                <h2 className="font-serif text-xl text-neutral-50 mb-3">
                    Rappels
                </h2>
                <ul className="text-sm text-neutral-400 space-y-2 leading-relaxed">
                    <li>
                        Le site est bilingue : les champs se remplissent en
                        français et en anglais côte à côte.
                    </li>
                    <li>
                        Les modifications sont visibles immédiatement sur le
                        site public.
                    </li>
                    <li>
                        Aucun montant, aucun tarif : les honoraires se
                        discutent lors d'une consultation.
                    </li>
                </ul>
            </div>
        </div>
    );
}
